"use client";

import { PriorityTicketsHeaderChip } from "@/components/features/automatic-streak";
import { CryptoComCtaButton } from "@/components/features/crypto-com-signup";
import { CurrencySelector } from "@/components/features/currency";
import { NotificationDropdown } from "@/components/features/notifications";
import { TreasureChestButton } from "@/components/features/rewards";
import { WalletButton } from "@/components/features/wallet";
import { Button } from "@/components/ui/button";
import { useIsImpersonating } from "@/hooks/user";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

/**
 * Header - Minimal top bar for desktop with notification bell, claim rewards, and wallet button.
 *
 * This is a compact header that sits at the top of the main content area.
 * Navigation is handled by the sidebar, so this only contains:
 * - Currency selector (for display currency preference)
 * - Notification bell (for campaign notifications)
 * - Claim Rewards button
 * - Wallet button
 */
export const Header = () => {
  const { isImpersonating, impersonatedAddress, remainingSeconds, stopImpersonating } = useIsImpersonating();

  const formattedTime = isImpersonating
    ? `${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, "0")}`
    : "";

  return (
    <header
      className={cn(
        "fixed top-0 z-sticky",
        "hidden md:flex",
        "items-center",
        "right-0",
        "justify-end",
        "md:left-[var(--sidebar-width)]",
        "md:w-[calc(100%-var(--sidebar-width))]",
        "md:peer-data-[state=collapsed]:left-[var(--sidebar-width-icon)]",
        "md:peer-data-[state=collapsed]:w-[calc(100%-var(--sidebar-width-icon))]",
        "h-11",
        "border-b border-cede-border-container",
        "bg-cede-bg-primary/50 backdrop-blur-md",
      )}
    >
      <div className="flex items-center gap-2 px-4">
        <CryptoComCtaButton />
        {isImpersonating && (
          <Button
            variant="destructive"
            size="small"
            className="shrink-0"
            data-private-block
            title={impersonatedAddress ?? undefined}
            onClick={stopImpersonating}
            rightIcon={<X className="h-3.5 w-3.5" />}
          >
            Impersonating {impersonatedAddress?.slice(0, 6)}...{impersonatedAddress?.slice(-4)} ({formattedTime})
          </Button>
        )}
        <PriorityTicketsHeaderChip />
        <TreasureChestButton />
        <CurrencySelector source="header" />
        <NotificationDropdown />
        <WalletButton size="small" metricsSource="header" />
      </div>
    </header>
  );
};
